import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link, useParams, Navigate } from "react-router-dom";
import { api, productImage, productThumb } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { MobileProductCard } from "../components/mobile/MobileProductCard";
import { CategoryGuides } from "../components/CategoryGuides";
import { CategoryBuyerGuide } from "../components/CategoryBuyerGuide";
import { HubExtras } from "../components/UseCaseHub";
import { SubcategoryGuide } from "../components/SubcategoryGuide";
import { RetractableCompare } from "../components/RetractableCompare";
import { subcategoryGuide } from "../lib/subcategoryGuides.mjs";
import { categoryGuide } from "../lib/categoryGuides.mjs";
import { useIsMobile } from "../hooks/useIsMobile";
import { Input } from "../components/ui/input";
import { Search, SlidersHorizontal, FolderTree, ArrowUpRight } from "lucide-react";
import { SERVICE_CATEGORIES, getCategoryBySlug, useServiceCategories, VIRTUAL_NAV_CATEGORIES } from "../lib/categories";
import { WeddingToolsStrip } from "../components/WeddingToolsStrip";
import WeddingCollection from "./wedding/WeddingCollection";
import {
  useSeoHead,
  breadcrumbSchema,
  collectionPageSchema,
  faqSchemaFromBody,
  cleanDisplayName,
} from "../lib/seo";

const DEFAULT_SERVICE_SLUG = "roll-labels";

// Legacy → current slug aliases. Lets us 301-redirect old SEO URLs without
// touching the underlying DB category slug. Add new mappings here only.
const CATEGORY_SLUG_ALIASES = {
  "marketing-collateral": "digital-print",
  "large-format": "retractables-banners",
};

/**
 * Sub-categories that have a hand-built SEO landing page elsewhere in the
 * app. Keyed by `${service_category}/${sub_category}`. When we render either
 * a SubCategoryCard for one of these OR a product grid scoped to one, we
 * surface a copper "View landing page →" pill so customers and ad-traffic
 * land on the canonical SEO URL.
 */
const SUB_LANDING_PAGES = {
  "retractables-banners/tents": { href: "/large-format/tents", label: "Tents landing page" },
};

function landingPageFor(serviceSlug, subSlug) {
  if (!serviceSlug || !subSlug) return null;
  return SUB_LANDING_PAGES[`${serviceSlug}/${subSlug}`] || null;
}

function ServiceTabs({ active, categories, isMobile }) {
  return (
    <div className="border-b border-white/10" data-testid="service-tabs" role="tablist">
      <div className={`container-page flex gap-1 ${isMobile ? "flex-nowrap overflow-x-auto scrollbar-none -mx-0" : "flex-wrap"}`}>
        {categories.map((c) => {
          const isActive = c.slug === active;
          return (
            <Link
              key={c.slug}
              to={`/shop/${c.slug}`}
              className={`relative px-5 py-4 font-mono text-[11px] tracking-[0.22em] uppercase font-bold transition-colors ${isMobile ? "shrink-0 whitespace-nowrap px-3 py-3" : ""} ${
                isActive ? "text-white" : "text-white/55 hover:text-white"
              }`}
              data-testid={`service-tab-${c.slug}`}
            >
              {c.name}
              {isActive && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-kolt-orange" />
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

// Card mirroring the visual rhythm of <ProductCard/> so the Category →
// Sub-category step feels like the same drill-down motion (per user spec).
function SubCategoryCard({ sub, parentSlug }) {
  const landing = landingPageFor(parentSlug, sub.slug);
  return (
    <Link
      to={`/shop/${parentSlug}/${sub.slug}`}
      className="group relative bg-white border border-slate-200 hover:border-kolt-orange transition-all duration-300 flex flex-col overflow-hidden"
      data-testid={`subcat-card-${sub.slug}`}
    >
      <div className="relative aspect-[4/3] bg-gradient-to-br from-slate-100 to-slate-200 overflow-hidden flex items-center justify-center">
        {sub.image_url ? (
          <img
            src={productThumb(sub.image_url, 600)}
            alt={sub.name}
            className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
            loading="lazy"
          />
        ) : (
          <FolderTree className="w-16 h-16 text-slate-400 group-hover:text-kolt-orange transition-colors" strokeWidth={1.2} />
        )}
        <span className="absolute top-3 left-3 font-mono text-[10px] tracking-[0.22em] uppercase font-bold bg-white/90 backdrop-blur text-kolt-slate px-2 py-1">
          Sub-category
        </span>
        {landing && (
          <Link
            to={landing.href}
            onClick={(e) => e.stopPropagation()}
            className="absolute top-3 right-3 font-mono text-[9px] tracking-[0.18em] uppercase font-bold bg-kolt-orange text-white px-2 py-1 hover:bg-[#9A3F12] transition-colors flex items-center gap-1"
            data-testid={`subcat-landing-${sub.slug}`}
          >
            Landing page <ArrowUpRight className="w-3 h-3" />
          </Link>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-display font-bold text-lg text-kolt-slate-dark leading-tight mb-2 group-hover:text-kolt-orange transition-colors">
          {cleanDisplayName(sub.name)}
        </h3>
        <p className="text-sm text-slate-500 leading-relaxed mb-4 flex-1">
          {sub.product_count > 0
            ? `${sub.product_count} product${sub.product_count === 1 ? "" : "s"} available`
            : "Coming soon"}
        </p>
        <div className="border-t border-slate-100 pt-4 flex items-center justify-between">
          <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-slate-400 font-bold">
            Browse
          </span>
          <ArrowUpRight className="w-5 h-5 text-kolt-slate group-hover:text-kolt-orange group-hover:rotate-12 transition-all" />
        </div>
      </div>
    </Link>
  );
}

export default function ShopPage() {
  const [params, setParams] = useSearchParams();
  const isMobile = useIsMobile();
  const routeParams = useParams();
  const [products, setProducts] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(params.get("search") || "");

  // True when the URL includes an explicit /shop/<category> path.
  // When it's just `/shop?search=xxx` (from the top-nav search), we do a
  // site-wide search instead of scoping to the DEFAULT category — otherwise
  // "presentation folders" typed in the header only searches roll labels.
  const hasExplicitCategory = !!routeParams.service_category || !!params.get("service");
  const urlServiceSlug =
    routeParams.service_category || params.get("service") || DEFAULT_SERVICE_SLUG;
  // Aliased URL slugs (e.g. "marketing-collateral" → "digital-print" in DB)
  // are resolved here so the rest of the page works against the canonical
  // DB slug.
  const activeService = CATEGORY_SLUG_ALIASES[urlServiceSlug] || urlServiceSlug;
  const virtualNav = VIRTUAL_NAV_CATEGORIES.find((v) => v.slug === urlServiceSlug && !routeParams.sub_category);
  const activeSubSlug = routeParams.sub_category || "";
  const { categories: liveCategories } = useServiceCategories();
  // Find the active category in the live list first; fall back to the
  // static map so the page still renders rich copy on first paint.
  const activeCategory =
    liveCategories.find((c) => c.slug === activeService) ||
    getCategoryBySlug(activeService) ||
    getCategoryBySlug(DEFAULT_SERVICE_SLUG) ||
    SERVICE_CATEGORIES[0];

  // Canonical URL slug used for href/canonical (prefers the aliased pretty
  // slug when present). Example: when the DB slug is "digital-print" but a
  // pretty alias "marketing-collateral" is mapped to it, the canonical URL
  // becomes /shop/marketing-collateral.
  const canonicalSlug = (() => {
    for (const [pretty, real] of Object.entries(CATEGORY_SLUG_ALIASES)) {
      if (real === activeService) return pretty;
    }
    return activeService;
  })();

  // 301-style permanent redirect: if the user requested the underlying DB
  // slug AND a pretty alias exists, send them to the pretty URL. Preserves
  // any active sub-category or search query.
  const needsRedirectTo = (() => {
    if (urlServiceSlug === canonicalSlug) return null;
    if (CATEGORY_SLUG_ALIASES[urlServiceSlug]) return null; // already on pretty
    if (canonicalSlug === urlServiceSlug) return null;
    const isAliasable = Object.values(CATEGORY_SLUG_ALIASES).includes(urlServiceSlug);
    if (!isAliasable) return null;
    const subPart = activeSubSlug ? `/${activeSubSlug}` : "";
    const query = params.toString();
    return `/shop/${canonicalSlug}${subPart}${query ? `?${query}` : ""}`;
  })();

  // Selected sub-category metadata (resolved against the fetched list — used
  // for the page H1 / breadcrumb crumb).
  const activeSub = useMemo(
    () => subcategories.find((s) => s.slug === activeSubSlug) || null,
    [subcategories, activeSubSlug]
  );

  // Fetch sub-categories for the active service whenever the customer flips
  // between top-level services. Cheap: one network call, ~10 docs typical.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/products/subcategories", {
          params: { service_category: activeService },
        });
        if (alive) setSubcategories(data || []);
      } catch (e) {
        if (alive) setSubcategories([]);
      }
    })();
    return () => { alive = false; };
  }, [activeService]);

  // Display mode: SHOW sub-category drill-down when (a) there ARE sub-cats
  // for this service AND (b) the customer has NOT picked one yet AND
  // (c) they're not searching.
  const searchQ = params.get("search") || "";
  const showSubcatDrilldown =
    !activeSubSlug && subcategories.length > 0 && !searchQ;

  // Fetch products. When a sub_category is in the URL OR no sub-cats exist
  // for this service yet, we list products. Otherwise we just show the
  // sub-category cards above and skip the product fetch.
  useEffect(() => {
    if (showSubcatDrilldown) {
      setProducts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    (async () => {
      try {
        // Only filter by service_category when the user is browsing a
        // specific /shop/<category> page. A bare /shop?search=xxx from the
        // top-nav search bar hits every product in the catalog.
        const apiParams = {};
        if (hasExplicitCategory) apiParams.service_category = activeService;
        if (activeSubSlug) apiParams.sub_category = activeSubSlug;
        if (searchQ) apiParams.search = searchQ;
        const { data } = await api.get("/products", { params: apiParams });
        setProducts(data || []);
      } catch (e) {
        console.error(e);
        setProducts([]);
      }
      setLoading(false);
    })();
  }, [activeService, activeSubSlug, searchQ, showSubcatDrilldown, hasExplicitCategory]);

  const submitSearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (search.trim()) next.set("search", search.trim()); else next.delete("search");
    setParams(next, { replace: true });
  };

  // ── SEO ────────────────────────────────────────────────────────────────
  const seoPath = (() => {
    const sub = activeSubSlug ? `/${activeSubSlug}` : "";
    return `/shop/${canonicalSlug}${sub}`;
  })();
  const subGuide = activeSubSlug ? subcategoryGuide(activeSubSlug) : null;
  const catGuide = !activeSub && hasExplicitCategory ? categoryGuide(activeService) : null;
  const richHero = !activeSub && !searchQ && catGuide?.hero ? catGuide : null;
  const seoTitle = subGuide
    ? `${subGuide.title} | Kolt Printing`
    : activeSub
      ? `${activeSub.name} — ${activeCategory.name || "Shop"} | Kolt Printing`
      : catGuide?.seoTitle
        ? `${catGuide.seoTitle} | Kolt Printing`
      : (activeCategory.meta_title || activeCategory.name)
        ? `${activeCategory.meta_title || activeCategory.name} — Kolt Printing`
        : "Shop — Kolt Printing";
  const seoDescription =
    subGuide?.description ||
    (!activeSub ? catGuide?.seoDescription : null) ||
    activeCategory.meta_description ||
    activeCategory.longDesc ||
    activeCategory.description ||
    activeCategory.short ||
    "";
  const seoImage = richHero ? `${window.location.origin}${richHero.hero}` : activeCategory.image_url ? productImage(activeCategory.image_url) : "";
  const collectionJsonLd = useMemo(() => ({
    id: "collection",
    data: collectionPageSchema({
      category: subGuide ? { ...activeCategory, meta_title: subGuide.title, meta_description: subGuide.description } : activeCategory,
      products,
      url: seoPath,
    }),
  }), [activeCategory, products, seoPath, subGuide]);
  const breadcrumbJsonLd = useMemo(() => {
    const crumbs = [
      { name: "Home", url: "/" },
      { name: "Shop", url: "/shop" },
      { name: activeCategory.name, url: `/shop/${canonicalSlug}` },
    ];
    if (activeSub) crumbs.push({ name: activeSub.name, url: `/shop/${canonicalSlug}/${activeSubSlug}` });
    return { id: "breadcrumb", data: breadcrumbSchema(crumbs) };
  }, [activeCategory, canonicalSlug, activeSub, activeSubSlug]);
  const faqJsonLd = useMemo(() => {
    const toLd = (faqs) => ({ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faqs.map(({ q, a }) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) });
    if (subGuide) return { id: "faq", data: toLd(subGuide.faqs) };
    if (catGuide?.faqs?.length) return { id: "faq", data: toLd(catGuide.faqs) };
    const data = faqSchemaFromBody(activeCategory.seo_content || activeCategory.description);
    return data ? { id: "faq", data } : null;
  }, [activeCategory, subGuide, catGuide]);
  useSeoHead({
    title: seoTitle,
    description: seoDescription,
    canonical: seoPath,
    image: seoImage,
    ogType: "website",
    jsonLd: [collectionJsonLd, breadcrumbJsonLd, faqJsonLd].filter(Boolean),
  });

  if (needsRedirectTo) {
    return <Navigate replace to={needsRedirectTo} />;
  }
  if (virtualNav) {
    return <Navigate replace to={virtualNav.href} />;
  }
  if (activeSubSlug === "wedding-collateral") {
    return (
      <WeddingCollection
        products={products}
        loading={loading}
        serviceTabs={<div className="bg-kolt-slate-dark text-white"><ServiceTabs active={activeService} categories={liveCategories} isMobile={isMobile} /></div>}
      />
    );
  }

  return (
    <main className="bg-kolt-paper-soft min-h-screen pb-20" data-testid="shop-page">
      <div className="relative bg-kolt-slate-dark text-white overflow-hidden">
        <div className="absolute inset-0 blueprint-grid-dense opacity-[0.07] pointer-events-none" />
        <div className="container-page py-12 lg:py-16 relative">
          {/* Same "Request a Custom Quote" callout as the landing hero — pinned top-right */}
          <Link
            to="/quote"
            className={`${richHero ? "hidden" : "hidden md:flex"} absolute right-4 top-6 lg:top-8 group flex-col items-start gap-1 bg-kolt-orange hover:bg-kolt-orange-hover text-white font-mono tracking-[0.24em] font-black uppercase pl-4 pr-4 py-3 shadow-[6px_6px_0_rgba(0,0,0,0.55)] hover:shadow-[3px_3px_0_rgba(0,0,0,0.55)] hover:translate-x-[3px] hover:translate-y-[3px] transition-all duration-150 z-10`}
            data-testid="category-quote-cta"
            aria-label="Request a custom quote"
          >
            <span className="flex items-center gap-2 text-[11px]">
              <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" aria-hidden />
              <span>Request a Custom Quote</span>
              <ArrowUpRight className="w-4 h-4 group-hover:rotate-12 transition-transform" />
            </span>
            <span className="text-[9px] tracking-[0.18em] font-normal normal-case text-white/80 pl-3.5">
              Human response within 2 hours
            </span>
          </Link>
          <Link
            to="/quote"
            className="md:hidden flex items-center justify-center gap-2 mb-5 bg-kolt-orange text-white font-mono text-[11px] tracking-[0.24em] font-black uppercase py-2.5 shadow-[4px_4px_0_rgba(0,0,0,0.55)]"
            data-testid="category-quote-cta-mobile"
          >
            Request a Custom Quote <ArrowUpRight className="w-4 h-4" />
          </Link>
          {/* Visible category breadcrumb (always present on /shop/:slug) */}
          <nav
            aria-label="Breadcrumb"
            className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55 mb-4"
            data-testid="category-breadcrumb"
          >
            <Link to="/" className="hover:text-white">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/shop" className="hover:text-white">Shop</Link>
            <span className="mx-2">/</span>
            <span className="text-white">
              {!hasExplicitCategory && searchQ ? `Search: "${searchQ}"` : activeCategory.name}
            </span>
            {activeSub && (
              <>
                <span className="mx-2">/</span>
                <span className="text-white">{cleanDisplayName(activeSub.name)}</span>
              </>
            )}
          </nav>
          <div className={richHero ? "grid lg:grid-cols-12 gap-8 lg:gap-12 items-center" : ""}>
          <div className={richHero ? "lg:col-span-6" : ""}>
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-12 bg-kolt-orange" />
            <span className="font-mono text-[11px] tracking-[0.3em] font-bold uppercase text-kolt-orange">
              {!hasExplicitCategory && searchQ
                ? "Search Results"
                : activeSub ? `${activeCategory.name} · Sub-category` : richHero?.kicker || "Capabilities Catalog"}
            </span>
          </div>
          <h1
            className={`font-display font-black text-4xl lg:text-5xl tracking-tight uppercase leading-[1.05] max-w-3xl ${richHero ? "" : "md:pr-72"}`}
            data-testid="shop-title"
          >
            {!hasExplicitCategory && searchQ
              ? `Results for "${searchQ}"`
              : activeSub ? cleanDisplayName(activeSub.name) : richHero?.h1 || activeCategory.name}
          </h1>
          <p className="text-white/65 mt-3 max-w-2xl leading-relaxed">
            {!hasExplicitCategory && searchQ
              ? `Every product across the Kolt Printing catalog matching "${searchQ}". Clear the search to browse by category.`
              : activeSub
              ? `Browse our ${activeSub.name.toLowerCase()} under ${activeCategory.name}.`
              : richHero?.lead || activeCategory.longDesc}
          </p>
          {richHero?.stats && (
            <dl className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 border border-white/10" data-testid="category-hero-stats">
              {richHero.stats.map((st) => (
                <div key={st.k} className="bg-kolt-slate-dark px-4 py-3">
                  <dt className="font-mono text-[9px] tracking-[0.22em] uppercase text-white/45">{st.k}</dt>
                  <dd className="font-display font-black text-lg text-white mt-0.5">{st.v}</dd>
                </div>
              ))}
            </dl>
          )}
          {richHero && (
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/quote" className="inline-flex items-center gap-2 bg-kolt-orange hover:bg-kolt-orange-hover text-white font-mono text-[11px] tracking-[0.24em] font-black uppercase px-5 py-3 shadow-[6px_6px_0_rgba(0,0,0,0.55)] hover:shadow-[3px_3px_0_rgba(0,0,0,0.55)] hover:translate-x-[3px] hover:translate-y-[3px] transition-all" data-testid="category-hero-quote">
                Request a Custom Quote <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link to="/same-day" className="inline-flex items-center gap-2 border border-white/30 hover:border-white text-white font-mono text-[11px] tracking-[0.24em] font-bold uppercase px-5 py-3 transition-colors" data-testid="category-hero-sameday">
                Same-day items
              </Link>
            </div>
          )}
          </div>
          {richHero && (
            <figure className="lg:col-span-6 relative mt-6 lg:mt-0" data-testid="category-hero-image">
              <div className="hidden lg:block absolute -inset-2 border border-kolt-orange/40 translate-x-3 translate-y-3 pointer-events-none" aria-hidden />
              <img src={richHero.hero} alt={richHero.heroAlt || activeCategory.name} width="1264" height="848"
                   className="relative w-full aspect-[3/2] object-cover shadow-[12px_12px_0_rgba(0,0,0,0.45)]" fetchpriority="high" />
            </figure>
          )}
          </div>
          {/* Breadcrumb when a sub-category is active */}
          {activeSub && (
            <div className="mt-5 flex items-center flex-wrap gap-4">
              <div className="text-xs font-mono uppercase tracking-[0.22em] text-white/55" data-testid="shop-breadcrumb">
                <Link to={`/shop/${activeService}`} className="hover:text-white">
                  ← Back to {activeCategory.name}
                </Link>
              </div>
              {landingPageFor(activeService, activeSubSlug) && (
                <Link
                  to={landingPageFor(activeService, activeSubSlug).href}
                  data-testid="shop-landing-pill"
                  className="inline-flex items-center gap-1.5 bg-kolt-orange hover:bg-[#9A3F12] text-white px-3 py-1.5 font-mono text-[10px] tracking-[0.22em] uppercase font-bold transition-colors"
                >
                  View {landingPageFor(activeService, activeSubSlug).label}
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              )}
            </div>
          )}
        </div>
        <ServiceTabs active={activeService} categories={liveCategories} isMobile={isMobile} />
      </div>

      <div className={`container-page ${isMobile ? "py-4" : "py-8"}`}>
        <div>
          <div
            className={`bg-white border border-slate-200 flex gap-3 ${
              isMobile
                ? "sticky top-[88px] z-30 -mx-4 px-4 py-2.5 mb-4 flex-row items-center border-x-0"
                : "p-4 mb-6 flex-col sm:flex-row sm:items-center sm:justify-between"
            }`}
            data-testid="shop-toolbar"
          >
            <form onSubmit={submitSearch} className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, material..."
                className="rounded-full pl-10 border-slate-300"
                data-testid="shop-search-input"
              />
            </form>
            <div className={`flex items-center gap-2 font-mono text-[11px] tracking-[0.22em] uppercase ${isMobile ? "shrink-0" : ""}`}>
              <SlidersHorizontal className="w-4 h-4 text-kolt-slate" />
              {showSubcatDrilldown ? (
                <>
                  <span className="font-bold text-kolt-slate-dark">{subcategories.length}</span>
                  {!isMobile && <span className="text-slate-500">sub-categories</span>}
                </>
              ) : (
                <>
                  <span className="font-bold text-kolt-slate-dark">{products.length}</span>
                  {!isMobile && <span className="text-slate-500">products</span>}
                </>
              )}
            </div>
          </div>

          {activeSubSlug === "retractables-roll-up-banners" && !loading && products.length > 0 && <RetractableCompare products={products} />}

          {showSubcatDrilldown ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-5" data-testid="subcategories-grid">
              {subcategories.map((s) => (
                <SubCategoryCard key={s.subcategory_id} sub={s} parentSlug={activeService} />
              ))}
            </div>
          ) : loading ? (
            <div className={`grid gap-5 ${isMobile ? "grid-cols-2 gap-3" : "grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"}`}>
              {[...Array(8)].map((_, i) => (
                <div key={`skeleton-${i}`} className={`bg-white border border-slate-200 animate-pulse ${isMobile ? "h-[240px]" : "h-[400px]"}`} />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="bg-white border border-slate-200 p-12 text-center" data-testid="no-products">
              <div className="font-display font-black text-2xl text-kolt-slate-dark uppercase mb-2">
                No matches
              </div>
              <p className="text-slate-500 text-sm mb-4">
                No products in this category yet.
              </p>
            </div>
          ) : isMobile ? (
            <div className="grid grid-cols-2 gap-3" data-testid="products-grid">
              {products.map((p) => <MobileProductCard key={p.product_id} product={p} />)}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-5" data-testid="products-grid">
              {products.map((p) => <ProductCard key={p.product_id} product={p} />)}
            </div>
          )}

          {/* SEO Body Content — long-form copy below the product grid. Pulls
              from admin-managed `seo_content` on the category if set; never
              displays unless content is present. */}
          {activeCategory.seo_content && !activeSub && (
            <section className="card-base p-6 mt-10" data-testid="category-seo-body">
              <div className="prose prose-slate max-w-none text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                {activeCategory.seo_content}
              </div>
            </section>
          )}
          {activeSubSlug === "wedding-collateral" && <WeddingToolsStrip />}
          {activeSub && <SubcategoryGuide subSlug={activeSubSlug} />}
          {!activeSub && activeService === "roll-labels" && <HubExtras basePath="/labels" />}
          {!activeSub && activeService === "retractables-banners" && <HubExtras basePath="/large-format" />}
          {!activeSub && <CategoryBuyerGuide categorySlug={activeService} />}
          <CategoryGuides categorySlug={activeService} categoryName={activeCategory.name || activeCategory.label || ""} />
        </div>
      </div>
    </main>
  );
}
